import type { Transcription } from 'js/src/client';
import { ApiService, SourceEnum, TranscriptAssetType } from 'js/src/client';

export const TRANSCRIPT_POLL_TIMEOUT_MS = 30_000;
export const TRANSCRIPT_POLL_INTERVAL_MS = 2_000;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export interface TranscriptResult {
    has_voiceover: boolean;
    voiceover_transcript?: string;
}

const joinWords = (words: { word?: unknown }[]): string =>
    words
        .map((entry) => (typeof entry.word === 'string' ? entry.word : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();

/**
 * Builds a plain-text transcript from the caption words already present on an asset's
 * `captions` field, so callers can use a transcript that's in hand instead of re-fetching
 * it. Returns undefined when there are no caption words.
 */
export function buildTranscriptFromCaptions(
    captions: Transcription | null | undefined,
): string | undefined {
    const words = captions?.captions;
    if (!words || words.length === 0) {
        return undefined;
    }
    return joinWords(words) || undefined;
}

/**
 * Transcription is generated asynchronously (Celery + Whisper) after an asset is
 * uploaded, so the transcript row may not exist yet when the upload resolves. Poll
 * the retrieve endpoint until words are available, up to TRANSCRIPT_POLL_TIMEOUT_MS.
 * An empty response is treated as "not ready yet" and keeps polling; if the deadline
 * passes without any words, we conclude there is no voiceover.
 */
export async function fetchTranscriptWithPolling(
    assetUuid: string,
    assetType: TranscriptAssetType,
): Promise<TranscriptResult> {
    const deadline = Date.now() + TRANSCRIPT_POLL_TIMEOUT_MS;

    do {
        try {
            const transcriptResponse = await ApiService.assetTranscriptRetrieve({
                path: { asset_id: assetUuid },
                query: { asset_type: assetType },
            });
            const words = transcriptResponse.data?.transcriptWords;
            if (words && words.length > 0) {
                return {
                    has_voiceover: true,
                    voiceover_transcript: joinWords(words),
                };
            }
        } catch {
            // Transcript not available yet — keep polling until the deadline.
        }

        if (Date.now() + TRANSCRIPT_POLL_INTERVAL_MS >= deadline) break;
        await delay(TRANSCRIPT_POLL_INTERVAL_MS);
    } while (Date.now() < deadline);

    return { has_voiceover: false };
}

// Sources whose transcript is generated asynchronously after the user uploads the file,
// so it may not exist yet at the moment the upload resolves.
const VIDEO_UPLOAD_SOURCES: ReadonlySet<SourceEnum> = new Set([
    SourceEnum.SOURCE_USER,
    SourceEnum.SOURCE_USER_OUTRO,
]);
const AUDIO_UPLOAD_SOURCES: ReadonlySet<SourceEnum> = new Set([
    SourceEnum.SOURCE_USER_VOICEOVER,
    SourceEnum.SOURCE_AI_VOICEOVER,
]);

export interface VoiceoverResolution {
    hasVoiceover: boolean | null;
    voiceoverTranscript?: string;
}

/**
 * Resolves the voiceover state and transcript for an audio or video asset, using the
 * data already in hand before falling back to the network:
 * - `captions` already populated: build the transcript from it, no network call.
 * - `has_voiceover === false`: no voiceover, nothing to fetch.
 * - non-upload media (stock/source): the transcript, if any, is already in `captions`;
 *   there is no async transcription to wait for, so never poll.
 * - a freshly uploaded asset whose transcript is still generating: poll until it lands
 *   (returns early once available, gives up at the timeout).
 */
export async function resolveVoiceoverTranscript(
    asset: {
        uuid?: string | null;
        source?: SourceEnum | null;
        has_voiceover?: boolean | null;
        captions?: Transcription | null;
    },
    assetType: TranscriptAssetType,
): Promise<VoiceoverResolution> {
    const responseHasVoiceover = asset.has_voiceover ?? null;

    const captionTranscript = buildTranscriptFromCaptions(asset.captions);
    if (captionTranscript) {
        return { hasVoiceover: true, voiceoverTranscript: captionTranscript };
    }

    const uploadSources =
        assetType === TranscriptAssetType.VIDEO ? VIDEO_UPLOAD_SOURCES : AUDIO_UPLOAD_SOURCES;
    const isUpload = asset.source != null && uploadSources.has(asset.source);
    if (!isUpload || responseHasVoiceover === false || !asset.uuid) {
        return { hasVoiceover: responseHasVoiceover };
    }

    const result = await fetchTranscriptWithPolling(asset.uuid, assetType);
    return {
        // Don't downgrade a response-confirmed voiceover to false just because polling
        // timed out without a transcript.
        hasVoiceover: result.has_voiceover || responseHasVoiceover === true,
        voiceoverTranscript: result.voiceover_transcript,
    };
}
